import React from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * GhostHoverButton — botão ghost com preenchimento no hover (Entrar, Ver Demo)
 *
 * Começa transparente com borda branca. No hover, um círculo expande e preenche.
 *
 * Quando showDot=false (padrão) → sem dot visível, expansão pelo centro
 * Quando showDot=true           → dot visível à esquerda, expansão pela posição do dot
 *
 * Props:
 *  children       {ReactNode}
 *  className      {string}     Classes extras para tamanho/fonte/layout
 *  fillColor      {string}     Cor de preenchimento no hover   default: #ffffff
 *  dotColor       {string}     Cor do dot (quando showDot=true) default: #ffffff
 *  textColor      {string}     Cor do texto no estado padrão   default: #ffffff
 *  textHoverColor {string}     Cor do texto no hover           default: #131315
 *  showDot        {boolean}    Exibe ponto visível à esquerda  default: false
 *  style          {object}     Inline styles extras
 */
export function GhostHoverButton({
  children,
  className = '',
  fillColor = '#ffffff',
  dotColor = '#ffffff',
  textColor = '#ffffff',
  textHoverColor = '#131315',
  showDot = false,
  style = {},
  ...props
}) {
  return (
    <button
      className={`group relative cursor-pointer overflow-hidden rounded-xl border border-white/20 bg-transparent px-6 py-3 text-center font-semibold transition-all ${className}`}
      style={{ color: textColor, ...style }}
      {...props}
    >
      {showDot ? (
        /* Modo com dot: ponto visível que expande ao escalar */
        <div className="flex items-center justify-center gap-2">
          <div
            className="h-2 w-2 flex-shrink-0 rounded-full transition-all duration-300 group-hover:scale-[100.8]"
            style={{ backgroundColor: dotColor || fillColor }}
          />
          <span className="inline-block transition-all duration-300 group-hover:translate-x-12 group-hover:opacity-0">
            {children}
          </span>
        </div>
      ) : (
        /* Modo sem dot: círculo invisível no centro expande para preencher */
        <div className="relative flex items-center justify-center">
          <div
            className="absolute left-1/2 top-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-500 group-hover:h-[300%] group-hover:w-[300%]"
            style={{ backgroundColor: fillColor }}
          />
          <span className="relative inline-block transition-all duration-300 group-hover:translate-x-12 group-hover:opacity-0">
            {children}
          </span>
        </div>
      )}

      {/* Hover reveal — slides in from right */}
      <div
        className="absolute top-0 z-10 flex h-full w-full translate-x-12 items-center justify-center gap-2 opacity-0 transition-all duration-300 group-hover:-translate-x-5 group-hover:opacity-100"
        style={{ color: textHoverColor }}
      >
        <span>{children}</span>
        <ArrowRight className="w-4 h-4" />
      </div>
    </button>
  );
}
